import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    Input,
    InputPassword,
    Modal,
    SegmentedControl,
    SparkIcon,
    SparkModalPortal,
    TooltipTrigger,
    sparkToast,
} from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import AzureBatchDeploymentsPicker from '@/features/settings/components/providers/AzureBatchDeploymentsPicker.jsx';
import RecommendedProviderRow from '@/features/settings/components/RecommendedProviderRow.jsx';
import '../settings.css';

const OPENAI_FIELDS = [
    {
        name: 'azure_api_base',
        label: 'Azure OpenAI Endpoint',
        required: true,
        secret: false,
        placeholder: 'https://<resource-name>.openai.azure.com/',
        info: 'Example: https://<resource-name>.openai.azure.com/ — find it in the Azure Portal under your Azure OpenAI resource → Keys and Endpoint.',
    },
    {
        name: 'azure_api_key',
        label: 'Azure API Key',
        required: true,
        secret: true,
        placeholder: 'Enter your Azure OpenAI API key',
    },
    {
        name: 'azure_batch_deployments',
        label: 'Global-Batch Deployments (optional - auto-detected when Azure credentials are available)',
        required: false,
        secret: false,
        picker: true,
        info:
            'Optional override. Global-Batch deployments are auto-detected when the platform '
            + 'has an Azure identity (az login, workload/managed identity) that can read this '
            + 'resource through ARM. When auto-detection is unavailable, pick or type the '
            + 'Global-Batch deployment names here. When a batch deployment matches the model '
            + 'behind your selected deployment, large evaluations run through the Azure Batch '
            + 'API (separate quota, no rate-limit throttling) instead of realtime calls.',
    },
];

const FOUNDRY_FIELDS = [
    {
        name: 'azure_foundry_project_endpoint',
        label: 'Project Endpoint',
        required: true,
        secret: false,
        placeholder: 'https://<project-name>.<region>.api.azureml.ms',
        info: 'Example: https://my-project.eastus2.api.azureml.ms — find it in Azure AI Foundry under your project → Overview → Project endpoint.',
    },
    {
        name: 'azure_tenant_id',
        label: 'Azure Tenant ID',
        required: true,
        secret: false,
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    },
    {
        name: 'azure_client_id',
        label: 'Azure Client ID',
        required: true,
        secret: false,
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    },
    {
        name: 'azure_client_secret',
        label: 'Azure Client Secret',
        required: true,
        secret: true,
        placeholder: 'Enter your Azure client secret',
    },
];

const isLikelyUrl = (value) => {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (err) {
        return false;
    }
};

const MODE_OPTIONS = [
    { id: 'openai', label: 'OpenAI Resource' },
    { id: 'foundry', label: 'Foundry Project' },
];

// ``?focus=`` deep-link param -> form field it lands on. The only current
// target is the Global-Batch Deployments picker (evaluation banner's
// "Add Azure Global-Batch deployment" fix), which lives on the OpenAI
// Resource form, so the deep link also forces that mode.
const FOCUS_FIELD_NAMES = {
    'batch-deployments': 'azure_batch_deployments',
};

const AzureCredentialsForm = ({
    credentials,
    onCredentialsUpdate,
    onCredentialsClear,
    recommendedProvider,
    onRecommendedProviderUpdate,
    focusField = null,
}) => {
    const { role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const focusFieldName = FOCUS_FIELD_NAMES[focusField] || null;
    const [mode, setMode] = useState(focusFieldName ? 'openai' : credentials?.azure_mode || 'openai');
    const [values, setValues] = useState(credentials || {});
    const [isSaving, setIsSaving] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [confirmingClear, setConfirmingClear] = useState(false);
    const focusInputRef = useRef(null);
    const focusAppliedRef = useRef(false);

    useEffect(() => {
        setValues(credentials || {});
        setMode(focusAppliedRef.current || !focusFieldName ? credentials?.azure_mode || 'openai' : 'openai');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credentials]);

    // Deep-link focus: land the caret on the requested field once, after the
    // OpenAI Resource form has rendered.
    useEffect(() => {
        if (!focusFieldName || focusAppliedRef.current || !focusInputRef.current) return;
        focusInputRef.current.focus();
        focusAppliedRef.current = true;
    });

    const activeFields = mode === 'foundry' ? FOUNDRY_FIELDS : OPENAI_FIELDS;

    const isDirty =
        mode !== (credentials?.azure_mode || 'openai') ||
        activeFields.some(
            (f) => (values[f.name] || '') !== (credentials?.[f.name] || '')
        );

    const hasSavedCredentials = [...OPENAI_FIELDS, ...FOUNDRY_FIELDS].some(
        (f) => Boolean(credentials?.[f.name])
    );

    const handleChange = (name, value) => {
        setValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        const trimmed = activeFields.reduce((acc, f) => {
            acc[f.name] = (values[f.name] || '').trim();
            return acc;
        }, {});

        const missing = activeFields.filter((f) => f.required && !trimmed[f.name]);
        if (missing.length > 0) {
            sparkToast.error(
                'Missing required fields',
                { content: missing.map((f) => f.label).join(', ') }
            );
            return;
        }

        const urlField =
            mode === 'foundry' ? 'azure_foundry_project_endpoint' : 'azure_api_base';
        if (!isLikelyUrl(trimmed[urlField])) {
            sparkToast.error(
                `${mode === 'foundry' ? 'Project Endpoint' : 'Azure OpenAI Endpoint'} must be a valid http(s) URL.`
            );
            return;
        }

        setIsSaving(true);
        trimmed.azure_mode = mode;
        onCredentialsUpdate(trimmed, 'saved');
        setTimeout(() => setIsSaving(false), 500);
    };

    const handleClear = () => {
        setConfirmingClear(false);
        setIsClearing(true);
        onCredentialsClear();
        setTimeout(() => setIsClearing(false), 500);
    };

    const renderFieldRow = (field) => {
        const input = field.picker ? (
            <div className="settings-field-group settings-batch-picker__group">
                <span className="settings-field-group__label">{field.label}</span>
                <AzureBatchDeploymentsPicker
                    value={values[field.name] || ''}
                    onChange={(next) => handleChange(field.name, next)}
                    endpoint={values.azure_api_base || ''}
                    apiKey={values.azure_api_key || ''}
                    disabled={!isAdmin}
                    inputRef={field.name === focusFieldName ? focusInputRef : undefined}
                />
            </div>
        ) : field.secret ? (
            <InputPassword
                size="large"
                label={field.label}
                showAsterisk={field.required}
                showPrefixIcon
                value={values[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                disabled={!isAdmin}
                className="settings-secret__input"
            />
        ) : (
            <Input
                ref={field.name === focusFieldName ? focusInputRef : undefined}
                size="large"
                label={field.label}
                showAsterisk={field.required}
                value={values[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                disabled={!isAdmin}
                className="settings-secret__input"
            />
        );

        if (!field.info) return <React.Fragment key={field.name}>{input}</React.Fragment>;

        return (
            <div className="settings-form__row settings-form__row--top" key={field.name}>
                {input}
                <TooltipTrigger text={field.info}>
                    <span className="settings-recommended__help settings-field-help">
                        <SparkIcon slug="circle-help" size="small" />
                    </span>
                </TooltipTrigger>
            </div>
        );
    };

    return (
        <div className="settings-providers__panel">
            <div className="settings-subheading-group">
                <h3 className="settings-subheading fs-unmask">AzureAI Credentials</h3>
                <p className="settings-text settings-providers__description fs-unmask">
                    {mode === 'foundry' ? (
                        <>
                            Configure Azure AI Foundry access so SpecificAI can call
                            models deployed on your Foundry project. Need help? See the
                            {' '}
                            <a
                                className="settings-link"
                                href="https://learn.microsoft.com/azure/ai-studio/how-to/sdk-generative-overview"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Azure AI Foundry quickstart
                            </a>
                            .
                        </>
                    ) : (
                        <>
                            Configure Azure OpenAI access so SpecificAI can call models
                            hosted on your Azure resource. Need help? See the
                            {' '}
                            <a
                                className="settings-link"
                                href="https://learn.microsoft.com/azure/ai-services/openai/quickstart"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Azure OpenAI quickstart
                            </a>
                            .
                        </>
                    )}
                </p>
            </div>

            <SegmentedControl
                items={MODE_OPTIONS.map((option) => ({
                    ...option,
                    disabled: !isAdmin,
                }))}
                activeId={mode}
                onChange={setMode}
                size="small"
            />

            <div className="settings-form">
                {activeFields.map(renderFieldRow)}
            </div>

            <div className="settings-form__footer settings-form__footer--gap">
                <Button
                    size="tiny"
                    variant="secondary"
                    danger
                    disabled={!isAdmin || !hasSavedCredentials || isClearing}
                    disableWhenReadOnly
                    onClick={() => setConfirmingClear(true)}
                >
                    Clear
                </Button>
                <Button
                    size="tiny"
                    variant="primary"
                    disabled={!isAdmin || !isDirty || isSaving}
                    disableWhenReadOnly
                    onClick={handleSave}
                >
                    Save Changes
                </Button>
            </div>

            {confirmingClear ? (
                <SparkModalPortal
                    open
                    onClose={() => setConfirmingClear(false)}
                    size="text"
                >
                    <Modal
                        title="Clear Azure credentials?"
                        subtitle="This removes all saved Azure credentials from your organization."
                        showScrollBar={false}
                        fixedHeight={false}
                        showOverlay={false}
                        danger
                        primaryLabel="Clear"
                        tertiaryLabel="Cancel"
                        primaryDisableWhenReadOnly
                        onPrimaryClick={handleClear}
                        onTertiaryClick={() => setConfirmingClear(false)}
                        onCloseClick={() => setConfirmingClear(false)}
                    />
                </SparkModalPortal>
            ) : null}

            <RecommendedProviderRow
                provider="azure"
                recommendedProvider={recommendedProvider}
                onRecommendedProviderUpdate={onRecommendedProviderUpdate}
            />
        </div>
    );
};

export default AzureCredentialsForm;
